/**
 * Builds the question catalogue from the parsed questionnaire, the codebook and
 * the routing. The only place that builds codebook paths: question label, then
 * loop iterations (outer first), then grid row, then the second grid dimension
 * or the option (facts.md, fact 9).
 */
import { HierarchicalDataEntry } from "types/engineOutput.types"
import {
  Catalogue,
  CatalogueInstance,
  CatalogueOption,
  CatalogueQuestion,
  QuestionType,
  RoutingInfo,
} from "../types/catalogue"
import { dataVariablesOf } from "./dataVariables"

type Raw = Record<string, unknown>
const isRecord = (v: unknown): v is Raw =>
  typeof v === "object" && v !== null && !Array.isArray(v)
const list = (v: unknown): Raw[] => (Array.isArray(v) ? v.filter(isRecord) : [])
const label = (v: Raw): string => (typeof v.label === "string" ? v.label : "")

/** The check response body, its `result` object, or the `parse` array itself. */
const variantRecords = (parse: unknown): Raw[] => {
  const body = isRecord(parse) && "result" in parse ? parse.result : parse
  const root = isRecord(body) && "parse" in body ? body.parse : body
  return list(root)
}

/** Variant names of a parse, in questionnaire order. */
export const variantsOf = (parse: unknown): string[] =>
  variantRecords(parse).flatMap((r) => Object.keys(r))

/** Every path of the codebook tree, labels joined with `//`. null before data import. */
export const codebookPaths = (
  codebook: HierarchicalDataEntry[] | null
): Set<string> | null => {
  if (!codebook) return null
  const out = new Set<string>()
  const walk = (entries: unknown, parent: string): void => {
    list(entries).forEach((e) => {
      if (typeof e.label !== "string") return
      const path = parent ? `${parent}//${e.label}` : e.label
      out.add(path)
      walk(e.value, path)
    })
  }
  walk(codebook, "")
  return out
}

const QUESTION_TYPES = ["QUESTION", "HIDDEN QUESTION", "DATA"]

const typeOf = (el: Raw): QuestionType | null => {
  const numeric = el.fillin === "NUMBER" || el.fillin === "REAL"
  switch (el["question type"]) {
    case "select one":
      return "select-one"
    case "select range":
      return "range"
    case "select many":
      return "select-many"
    case "per select one":
      return "grid-one"
    case "per select range":
      return "grid-range"
    case "per select many":
      return "grid-many"
    case "fillin":
      return numeric ? "number" : el.fillin === "TEXT" ? "text" : null
    case "list fillin":
    case "grid fillin":
      return numeric ? "grid-number" : el.fillin === "TEXT" ? "text" : null
    default:
      return null
  }
}

/**
 * Options of `select`. A `range` is expanded to one option per whole number;
 * the parse lists only the labelled points. An exception written inline in the
 * question is not marked by the parse and stays a normal option.
 */
const optionsOf = (el: Raw): { options: CatalogueOption[]; ordinal: boolean } => {
  const select = isRecord(el.select) ? el.select : null
  if (!select) return { options: [], ordinal: false }
  const elements = list(select.elements)
  const lateGroups = new Set(
    list(select.sorting)
      .slice(1)
      .flatMap((g) => (Array.isArray(g.elements) ? g.elements : []))
      .filter((x): x is string => typeof x === "string")
  )
  const isException = (e: Raw): boolean =>
    e["owning list"] === "EXCEPTIONS" || lateGroups.has(label(e))
  const range =
    Array.isArray(select.range) &&
    typeof select.range[0] === "number" &&
    typeof select.range[1] === "number"
      ? (select.range as [number, number])
      : null

  const toOption = (e: Raw): CatalogueOption => ({
    label: label(e),
    ...(typeof e.code === "number" ? { code: e.code } : {}),
    exception: isException(e),
  })
  if (!range) return { options: elements.filter(label).map(toOption), ordinal: false }

  const byCode = new Map<number, Raw>()
  elements.forEach((e) => {
    if (typeof e.code === "number" && !isException(e)) byCode.set(e.code, e)
  })
  const points: CatalogueOption[] = []
  for (let code = range[0]; code <= range[1]; code++) {
    const e = byCode.get(code)
    points.push({ label: e ? label(e) : String(code), code, exception: false })
  }
  const exceptions = elements.filter((e) => isException(e) && label(e)).map(toOption)
  return { options: [...points, ...exceptions], ordinal: true }
}

/**
 * The label of every question of every variant, whatever its type, walking
 * IF and FOR EACH blocks as `buildCatalogue` does. None of them is a variable
 * of the data, even when the catalogue cannot tabulate it.
 */
const allQuestionLabels = (records: Raw[]): Set<string> => {
  const out = new Set<string>()
  const walk = (els: Raw[]): void =>
    els.forEach((el) => {
      if (Array.isArray(el.block)) walk(list(el.block))
      else if (QUESTION_TYPES.includes(String(el.type)) && label(el)) out.add(label(el))
    })
  records.forEach((r) => Object.values(r).forEach((els) => walk(list(els))))
  return out
}

const cross = (lists: string[][]): string[][] =>
  lists.reduce<string[][]>(
    (acc, items) => acc.flatMap((head) => items.map((item) => [...head, item])),
    [[]]
  )

/**
 * The catalogue of one variant (`variant: null` = the first). Questions come
 * in questionnaire order; a second question with the same label is left out.
 * The codebook's variables that are not questions of any variant are its
 * data variables (`model/dataVariables.ts`).
 */
export const buildCatalogue = (args: {
  parse: unknown
  variant: string | null
  codebook: HierarchicalDataEntry[] | null
  routing: RoutingInfo | null
}): Catalogue => {
  const knownPaths = codebookPaths(args.codebook)
  const records = variantRecords(args.parse)
  const wanted = args.variant ?? variantsOf(args.parse)[0] ?? null
  const elements =
    wanted === null ? [] : list(records.find((r) => wanted in r)?.[wanted])

  const questions: CatalogueQuestion[] = []
  const byLabel = new Map<string, CatalogueQuestion>()

  // Depth first: IF and FOR EACH hold their content in `block`. A FOR EACH
  // adds its driver to the loop chain of everything inside it.
  const visit = (els: Raw[], chain: string[]): void => {
    els.forEach((el) => {
      if (el.type === "FOR EACH") {
        const driver = typeof el.reference === "string" ? el.reference : ""
        visit(list(el.block), driver ? [...chain, driver] : chain)
        return
      }
      if (Array.isArray(el.block)) {
        visit(list(el.block), chain)
        return
      }
      if (!QUESTION_TYPES.includes(String(el.type))) return
      const name = label(el)
      const type = typeOf(el)
      if (!name || !type || byLabel.has(name)) return

      const { options, ordinal } = optionsOf(el)
      // Iterations are the driver's options without its exceptions. A driver
      // not walked yet gives none, and the question is then not looped.
      const iterations = chain.map((driver) =>
        (byLabel.get(driver)?.options ?? []).filter((o) => !o.exception).map((o) => o.label)
      )
      const looped = iterations.length > 0 && iterations.every((i) => i.length > 0)
      const dimensions: string[][] = looped ? [...iterations] : []
      // A name per dimension: each loop's driver, then "Rows" and "Columns".
      const dimensionNames: string[] = looped ? [...chain] : []
      /** Where the grid rows are among the dimensions; none without them. */
      let rowsDimension: number | undefined
      // A text list or text grid has one field per row (and column), like a
      // numeric grid. A plain text question has no `per` and stays one field.
      if (type.startsWith("grid-") || type === "text") {
        const rows = list(isRecord(el.per) ? el.per.elements : []).map(label).filter(Boolean)
        if (rows.length > 0) {
          rowsDimension = dimensions.length
          dimensions.push(rows)
          dimensionNames.push("Rows")
        }
        const second = list(isRecord(el["and per"]) ? el["and per"].elements : [])
          .map(label)
          .filter(Boolean)
        if ((type === "grid-number" || type === "text") && second.length > 0) {
          dimensions.push(second)
          dimensionNames.push("Columns")
        }
      }
      const selectable =
        options.length > 0 && type !== "number" && type !== "grid-number" && type !== "text"
      const routed = args.routing?.questions[name]
      const instances: CatalogueInstance[] = cross(dimensions).map((parts) => {
        const path = [name, ...parts].join("//")
        const optionPaths = selectable
          ? Object.fromEntries(options.map((o) => [o.label, `${path}//${o.label}`]))
          : {}
        const inData = !knownPaths
          ? false
          : selectable
            ? Object.values(optionPaths).some((p) => knownPaths.has(p))
            : knownPaths.has(path)
        // The server gives one condition per question, for the first iteration
        // only, so a question in a loop gets no condition.
        const usable = routed && !routed.looped && !looped
        return {
          id: parts.join("//"),
          parts,
          label: parts.join(" - "),
          path,
          optionPaths,
          asked: usable ? routed.asked : null,
          coverage: !routed ? null : usable ? routed.coverage : "unsupported",
          offPath: usable ? routed.offPath : 0,
          missingOnPath: usable ? routed.missingOnPath : 0,
          inData,
        }
      })
      const texts = list(el.text)
      const question: CatalogueQuestion = {
        label: name,
        text: typeof texts[0]?.text === "string" ? texts[0].text : "",
        type,
        hidden: el.type !== "QUESTION",
        ordinal,
        options,
        instances,
        dimensions: dimensionNames,
        ...(rowsDimension !== undefined ? { rowsDimension } : {}),
      }
      byLabel.set(name, question)
      questions.push(question)
    })
  }
  visit(elements, [])

  return {
    questions,
    knownPaths,
    hasRouting: args.routing !== null,
    dataVariables: dataVariablesOf(args.codebook, allQuestionLabels(records)),
  }
}

/**
 * The catalogue of a project without a questionnaire: no questions, every
 * usable codebook variable a data variable, no routing. Before data import it
 * has no variables and `knownPaths` is null.
 */
export const buildDataOnlyCatalogue = (codebook: HierarchicalDataEntry[] | null): Catalogue => ({
  questions: [],
  dataVariables: dataVariablesOf(codebook, new Set()),
  knownPaths: codebookPaths(codebook),
  hasRouting: false,
  dataOnly: true,
})
